import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  Typography,
} from "@mui/material";
import { AtkBoardRelaySubform } from "./AtkBoardIo";
import { AutonomousActivitiesSubform } from "./AutonomousActivities";
import { buildPermission } from "common/utils/auth";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { IoLinkSubform } from "./IoLink";
import { JobsSubform } from "./JobsSubform";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { RigDetailsFormState } from "../FormState";
import { ScvAutonomySubform } from "./ScvAutonomy";
import { ThreePointAutonomySubform } from "./ThreePointAutonomy";
import { typedPath } from "typed-path";
import { useAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React from "react";

const S = typedPath<RigDetailsFormState>();

export const AtkConfigurationForm: React.FC<{ readOnly: boolean }> = ({
  readOnly,
}) => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  // fixme: use world-write as a proxy for access to the advanced configuration
  // options for now
  const canAccessAdvancedConfiguration = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.atk_equipment,
      PermissionDomain.all
    )
  );

  return (
    <Card variant="outlined" className="w-full rounded-xl min-h-40">
      <Typography variant="h6" className="m-5">
        {t("views.autotractor.rigDetails.configuration.title")}
      </Typography>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t(
            "views.autotractor.rigDetails.configuration.speedAndSteering.sectionHeader"
          )}
        </AccordionSummary>
        <AccordionDetails className="grid grid-cols-2 gap-4">
          <FormikDimensionedNumberInput
            label={t(
              "views.autotractor.rigDetails.configuration.speedAndSteering.maxSpeed"
            )}
            name={S.atkConfig.atkSettings.maxSpeedMph.$path}
            validate={true}
            tooltipText={t(
              "views.autotractor.rigDetails.configuration.speedAndSteering.maxSpeedTooltip"
            )}
            dimensionedNumberInputProps={{
              storedUnit: "mph",
              displayUnits: {
                metric: "km/h",
                imperial: "mph",
              },
              localSystem: measurementSystem,
              disabled: readOnly,
            }}
            className="w-full self-end"
          />
          <FormikDimensionedNumberInput
            label={t(
              "views.autotractor.rigDetails.configuration.speedAndSteering.maxSpeedSafetyBypassed"
            )}
            name={S.atkConfig.atkSettings.maxSpeedSafetyBypassedMph.$path}
            validate={true}
            tooltipText={t(
              "views.autotractor.rigDetails.configuration.speedAndSteering.maxSpeedSafetyBypassedTooltip"
            )}
            dimensionedNumberInputProps={{
              storedUnit: "mph",
              displayUnits: {
                metric: "km/h",
                imperial: "mph",
              },
              localSystem: measurementSystem,
              disabled: readOnly,
            }}
            className="w-full self-end"
          />
        </AccordionDetails>
      </Accordion>
      <JobsSubform readOnly={readOnly} />
      <AutonomousActivitiesSubform readOnly={readOnly} />
      <ThreePointAutonomySubform readOnly={readOnly} />
      <ScvAutonomySubform readOnly={readOnly} />
      {canAccessAdvancedConfiguration && (
        <>
          <IoLinkSubform readOnly={readOnly} />
          <AtkBoardRelaySubform readOnly={readOnly} />
        </>
      )}
    </Card>
  );
};
